import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useMediaQuery, useTheme } from '@mui/material';
import FullNameTextField from '~/app/application/features/components/full-name-text-field/full-name-text-field';
import PasswordTextField from '~/app/application/features/components/password-text-field/password-text-field';
import PhoneTextField from '~/app/application/features/components/phone-text-field/phone-text-field';
import { logEvent } from '~/app/application/shared/lib/tag';
import BaseRadioButton from '~/app/core/presentation/components/base-radio-button/base-radio-button';
import { useAuth } from '~/app/core/presentation/hooks/use-auth';
import { useFeatureFlags } from '~/app/core/presentation/hooks/use-feature-flags';

import BaseAlert from '../../../../../../../core/presentation/components/base-alert/base-alert';
import BaseButton from '../../../../../../../core/presentation/components/base-button/base-button';
import EmailTextField from '../../../../../components/email-text-field/email-text-field';
import { SignUpModel } from '../../../../domain/models/sign-up-model';
import { RegisterRoutes } from '../../../../routes/routes';
import { useRegisterUserDataStore } from '../../../../store/slice/register-user-data-slice';
import LoginOrRegisterWithGoogle from '../../../access-google/access-google';
import { carQuantity } from '../company-data-step/car-quantity';
import { SignUpFormDataProps } from '../form-register';
import { RegisterFormWizardTitle } from '../form-register-styles';
import ConfirmEmailStep from './confirm-email-step/confirm-email-step';
import ContactEnterpriseStep from './contact-enterprise-step/contact-enterprise-step';
import PersonalDataStepFooter from './personal-data-step-footer/personal-data-step-footer';
import { PersonalDataValidator } from './personal-data-step-footer/personal-data.validator';
import {
  NameContainer,
  PersonalDataStepContainer,
  QuantityCarContainer
} from './personal-data-step-styles';

type PersonalDataProps = {
  handleFormData: (values: SignUpFormDataProps) => void;
  formData: SignUpFormDataProps;
  comeBack: (data: { onClick: () => void }) => void;
};

const PersonalDataStep = ({
  handleFormData,
  formData,
  comeBack
}: PersonalDataProps) => {
  const [displayError, setDisplayError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setRegisterState } = useRegisterUserDataStore();
  const { t } = useTranslation('register-personal-step');
  const { useLogin } = useAuth();
  const { isFlagEnabled } = useFeatureFlags();
  const useFlowB = isFlagEnabled('use_signup_flow_b');
  const useGoogleLogin = isFlagEnabled('use_google_auth');
  const [showButtonLoginGoogle, setShowButtonLoginGoogle] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { registerState } = useRegisterUserDataStore();
  const [openContactEnterprise, setOpenContactEnterprise] = useState(false);
  const [showConfirmEmail, setShowConfirmEmail] = useState(false);
  const validFleetSizes = ['41 a 99', '100 a 500', 'Acima de 500'];

  const signUpRequest = useCallback(
    async (values: SignUpModel) => {
      const apiAuthUrl = process.env.API_AUTH_URL;
      const signUpUrl = `${apiAuthUrl}auth/sign-up`;

      setIsLoading(true);
      try {
        const response = await fetch(signUpUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            firstName: values.firstName,
            lastName: values.lastName,
            username: values.username,
            email: values.email,
            phoneNumber: values.phoneNumber,
            password: values.password,
            metaData: values.metaData
          })
        });

        if (response.status === 428) {
          setIsLoading(false);
          handleFormData({ ...formData, user: values });
          setShowConfirmEmail(true);
          return;
        }

        if (!response.ok) {
          setIsLoading(false);
          throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();

        useLogin({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken
        });
        setDisplayError(false);
        handleFormData({ ...formData, user: values, isAuthenticated: true });
        setRegisterState({
          isEnterpriseContact: false,
          user: {
            active: true,
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            phone: values.phone
          },
          carQuantity: values.carQuantity,
          isAuthenticated: true
        });
        logEvent({
          action: 'OnSubmit',
          label: 'step01_cadastro',
          category: 'Nome do evento/trigger'
        });

        setIsLoading(false);

        if (validFleetSizes.includes(values.carQuantity)) {
          comeBack({ onClick: () => setOpenContactEnterprise(false) });
          return setOpenContactEnterprise(true);
        }
        return navigate(RegisterRoutes.COMPANY_DATA);
      } catch (error) {
        setIsLoading(false);
        setDisplayError(true);
        console.error('Erro no sign-up:', error);
      }
    },
    [
      formData,
      handleFormData,
      useLogin,
      setRegisterState,
      navigate,
      validFleetSizes,
      comeBack
    ]
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isDirty, isValid }
  } = useForm<SignUpModel>({
    resolver: PersonalDataValidator,
    values: formData.user,
    mode: 'onChange'
  });

  const handleSubmitForm = useCallback(
    (data: SignUpModel) => {
      if (data.fullName) {
        const fullName = data.fullName.split(' ');
        data.firstName = fullName[0].trim();
        data.lastName = fullName.slice(1).join(' ').trim();
      }

      if (data.firstName && data.lastName) {
        data.username = `${data.firstName}_${data.lastName}`.toLowerCase();
      }

      signUpRequest({
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username,
        email: data.email,
        phoneNumber: data.phone,
        password: data.password,
        metaData: { flow: useFlowB ? 'B' : 'A' }
      });
    },
    [signUpRequest, useFlowB]
  );

  const displayFields = useMemo(() => {
    return (
      !useFlowB ||
      (registerState?.user?.firstName === '' &&
        registerState?.user?.lastName === '')
    );
  }, [registerState, useFlowB]);

  useEffect(() => {
    setShowButtonLoginGoogle(useGoogleLogin);
  }, [useGoogleLogin]);

  useEffect(() => {
    if (registerState?.user.firstName && registerState?.user.lastName) {
      setValue(
        'fullName',
        `${registerState.user.firstName} ${registerState.user.lastName}`
      );
      setValue('firstName', registerState.user.firstName);
      setValue('lastName', registerState.user.lastName);
      setValue('email', registerState.user.email);
      setValue('carQuantity', registerState.carQuantity);
      setValue('phone', registerState.user.phone);
    }
  }, []);

  return (
    <>
      {showConfirmEmail ? (
        <ConfirmEmailStep
          formData={formData}
          comeBack={(data) => {
            setShowConfirmEmail(false);
            comeBack(data);
          }}
        />
      ) : openContactEnterprise ? (
        <ContactEnterpriseStep comeBack={comeBack} />
      ) : (
        <PersonalDataStepContainer
          component='form'
          onSubmit={handleSubmit(handleSubmitForm)}
        >
          <RegisterFormWizardTitle>
            {t('titleRegister')}
          </RegisterFormWizardTitle>
          <LoginOrRegisterWithGoogle
            isRegister
            showButton={showButtonLoginGoogle}
          />

          {displayError && (
            <BaseAlert sx={{ width: '100%' }} severity='info'>
              {t('textEmailAlreadyExists')} <a href='/login'>entrar</a>?
            </BaseAlert>
          )}
          {displayFields && (
            <NameContainer>
              {/*
             <FirstNameTextField
               errorText={t('firstNameInvalid')}
               control={control}
             />
             <LastNameTextField
               errorText={t('lastNameInvalid')}
               control={control}
             />
           */}

              <FullNameTextField control={control} />
            </NameContainer>
          )}
          {registerState?.user?.firstName !== '' &&
            registerState?.user?.lastName !== '' && (
              <EmailTextField errorText={t('emailInvalid')} control={control} />
            )}
          {displayFields && (
            <PasswordTextField
              errorText={t('passwordInvalid')}
              control={control}
            />
          )}
          {displayFields && <PhoneTextField control={control} />}
          <QuantityCarContainer>
            <BaseRadioButton
              required
              row={!isMobile}
              control={control}
              name='carQuantity'
              labelid='carQuantity'
              label={t('carQuantity')}
              values={carQuantity}
            />
          </QuantityCarContainer>
          <BaseButton
            disableElevation
            type='submit'
            variant='contained'
            color='primary'
            disabled={!isDirty || !isValid}
            isLoading={isLoading}
          >
            {t('register:next')}
          </BaseButton>
          <PersonalDataStepFooter />
        </PersonalDataStepContainer>
      )}
    </>
  );
};

export default PersonalDataStep;
